// ===================================================
// src/components/Planning/PlanningTable.js
// SMART PROJECTED STOCK BY REAL ARRIVAL DATES (MRP LOGIC)
// + ⚙️ Visibility Control (Saved in localStorage)
// + 🧮 Real Yesterday Base from API
// ===================================================
import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Edit, Plus, Trash2, Settings } from "lucide-react";
import ProjectedInventoryChart from "./ProjectedInventoryChart";
import AdjustPercentagesModal from "./AdjustPercentagesModal";
import SimulationBoard from "./SimulationBoard";

const PlanningTable = ({
  allData,
  loading,
  error,
  stats,
  onEditDay,
  onAddFirstDay,
  onDeleteDay,
  onFilteredData,
  manualStockOverrides,
  setManualStockOverrides,
}) => {



  // ==============================
  // ⚙️ Visibility toggle with localStorage
  // ==============================
  const defaultSections = {
    salesForecast: false,
    actualSales: false,
    productionPlan: true,
    actualMFG: false,
    stock: true,
    shipment: true,
    duration: true,
  };

  const [visibleSections, setVisibleSections] = useState(() => {
    const saved = localStorage.getItem("planning_visible_sections");
    return saved ? JSON.parse(saved) : defaultSections;
  });

  useEffect(() => {
    localStorage.setItem(
      "planning_visible_sections",
      JSON.stringify(visibleSections)
    );
  }, [visibleSections]);

  // 🧮 Load percentage splits (Sales / Production / M-L)
  const [percentages, setPercentages] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [showPercentagesModal, setShowPercentagesModal] = useState(false);
  const [inTransit, setInTransit] = useState([]);
  const [showSimulation, setShowSimulation] = useState(false);


  useEffect(() => {
  axios
    .get(`${process.env.REACT_APP_API_URL}/api/factory-shipments/in-transit`)
    .then((res) => setInTransit(res.data.results || []))
    .catch((err) => console.error("❌ Failed to load in-transit shipments:", err));
}, []);

  useEffect(() => {
    axios
      .get("/api/plan/percentages")
      .then((res) => setPercentages(res.data))
      .catch((err) => console.error("❌ Failed to load percentages:", err));
  }, [refreshTrigger]);

  const [showConfig, setShowConfig] = useState(false);

    // ✅ restore sectionLabels
  const sectionLabels = {
    salesForecast: "📈 Sales Forecast Plan",
    actualSales: "📊 Actual Sales",
    productionPlan: "🏭 Production Plan",
    actualMFG: "Actual MFG",
    stock: "📦 Stock (Updated with Real Arrival Logic)",
    shipment: "🚚 Shipment",
    duration: "🕒 Shipment Duration (Days)",
  };

  // 🧩 new state for yesterday data
  const [baseDayData, setBaseDayData] = useState(null);

  // ===================================================
  // ⚙️ Fetch yesterday's real base from API
  // ===================================================
  useEffect(() => {
    const fetchYesterday = async () => {
      try {
        const today = new Date();
        today.setDate(today.getDate() - 1);
        const dateISO = today.toISOString().split("T")[0];
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/plan/day/${dateISO}`
        );
        setBaseDayData(res.data);
        console.log("✅ Loaded base day data:", res.data);
      } catch (err) {
        console.error("❌ Failed to fetch yesterday base:", err);
      }
    };
    fetchYesterday();

  }, []);


// ===============================================
// 📦 Smart Future Stock Projection (Dynamic % + Real Arrival Logic)
// ===============================================
const filteredData = useMemo(() => {
  if (!Array.isArray(allData) || !baseDayData?.detailed || !percentages) return [];

  const sorted = [...allData].sort((a, b) => new Date(a.date) - new Date(b.date));
  const todayISO = new Date().toISOString().split("T")[0];

  const salesSplit = percentages.sales || {};
  const prodSplit = percentages.production || {};
  const mlSalesSplit = percentages.ml_sales || {};
  const mlProdSplit = percentages.ml_production || {};

  // === Base stocks ===
  let running = {
    us_cityplay_m: baseDayData.detailed.stock_us_cityplay_m || 0,
    us_cityplay_l: baseDayData.detailed.stock_us_cityplay_l || 0,
    us_20_m: baseDayData.detailed.stock_us_20_m || 0,
    us_20_l: baseDayData.detailed.stock_us_20_l || 0,
    uk_cityplay_m: baseDayData.detailed.stock_uk_cityplay_m || 0,
    uk_cityplay_l: baseDayData.detailed.stock_uk_cityplay_l || 0,
    uk_20_m: baseDayData.detailed.stock_uk_20_m || 0,
    uk_20_l: baseDayData.detailed.stock_uk_20_l || 0,
  };

  let runningAmazon = {
    us_cityplay_m: baseDayData.detailed.stock_amazon_us_cityplay_m || 0,
    us_cityplay_l: baseDayData.detailed.stock_amazon_us_cityplay_l || 0,
    us_20_m: baseDayData.detailed.stock_amazon_us_20_m || 0,
    us_20_l: baseDayData.detailed.stock_amazon_us_20_l || 0,
    uk_cityplay_m: baseDayData.detailed.stock_amazon_uk_cityplay_m || 0,
    uk_cityplay_l: baseDayData.detailed.stock_amazon_uk_cityplay_l || 0,
    uk_20_m: baseDayData.detailed.stock_amazon_uk_20_m || 0,
    uk_20_l: baseDayData.detailed.stock_amazon_uk_20_l || 0,
  };

  // ✅ Normalize and align manual stock overrides to production dates
  const adjustedOverrides = {};
  Object.entries(manualStockOverrides || {}).forEach(([arrivalDate, skus]) => {
    Object.entries(skus).forEach(([sku, qty]) => {
      if (typeof qty !== "number" || !qty) return;

      const isUK = sku.startsWith("uk_") || sku.startsWith("uk_amazon_");
      const daysToSubtract = isUK ? 7 : 6;
      const realProdDate = new Date(arrivalDate);
      realProdDate.setDate(realProdDate.getDate() - daysToSubtract);
      const realDateISO = realProdDate.toISOString().split("T")[0];

      // ✅ Clean up SKU naming to a single format
      const cleanSku = sku
        .replace(/^eu_/, "uk_")
        .replace(/^us_amazon_/, "amazon_us_")
        .replace(/^uk_amazon_/, "amazon_uk_");

      if (!adjustedOverrides[realDateISO]) adjustedOverrides[realDateISO] = {};
      adjustedOverrides[realDateISO][cleanSku] =
        (adjustedOverrides[realDateISO][cleanSku] || 0) + qty;
    });
  });

  // === Simulation ===
  const simulated = sorted.map((day) => {
    const isFuture = day.date > todayISO;
    const isToday = day.date === todayISO;
    if (!isFuture && !isToday) return day;

    const totalProd = day.forecast?.production_total || day.manufacturing?.plan_target_total || 0;
    const totalSales = day.sales?.target_total || 0;

    // === Production split ===
    const prodGroups = {
      us_cityplay: (totalProd * (prodSplit.us_cityplay || 0)) / 100,
      us_20: (totalProd * (prodSplit.us_20 || 0)) / 100,
      uk_cityplay: (totalProd * (prodSplit.uk_cityplay || 0)) / 100,
      uk_20: (totalProd * (prodSplit.uk_20 || 0)) / 100,
    };

    const prodML = {
      us_cityplay_m: Math.round((prodGroups.us_cityplay * (mlProdSplit.us_cityplay?.m || 50)) / 100),
      us_cityplay_l: Math.round((prodGroups.us_cityplay * (mlProdSplit.us_cityplay?.l || 50)) / 100),
      us_20_m: Math.round((prodGroups.us_20 * (mlProdSplit.us_20?.m || 50)) / 100),
      us_20_l: Math.round((prodGroups.us_20 * (mlProdSplit.us_20?.l || 50)) / 100),
      uk_cityplay_m: Math.round((prodGroups.uk_cityplay * (mlProdSplit.uk_cityplay?.m || 50)) / 100),
      uk_cityplay_l: Math.round((prodGroups.uk_cityplay * (mlProdSplit.uk_cityplay?.l || 50)) / 100),
      uk_20_m: Math.round((prodGroups.uk_20 * (mlProdSplit.uk_20?.m || 50)) / 100),
      uk_20_l: Math.round((prodGroups.uk_20 * (mlProdSplit.uk_20?.l || 50)) / 100),
    };

    // === Sales split ===
    const salesGroups = {
      us_cityplay: (totalSales * (salesSplit.us_cityplay || 0)) / 100,
      us_20: (totalSales * (salesSplit.us_20 || 0)) / 100,
      uk_cityplay: (totalSales * (salesSplit.uk_cityplay || 0)) / 100,
      uk_20: (totalSales * (salesSplit.uk_20 || 0)) / 100,
    };

    const salesML = {
      us_cityplay_m: Math.round((salesGroups.us_cityplay * (mlSalesSplit.us_cityplay?.m || 50)) / 100),
      us_cityplay_l: Math.round((salesGroups.us_cityplay * (mlSalesSplit.us_cityplay?.l || 50)) / 100),
      us_20_m: Math.round((salesGroups.us_20 * (mlSalesSplit.us_20?.m || 50)) / 100),
      us_20_l: Math.round((salesGroups.us_20 * (mlSalesSplit.us_20?.l || 50)) / 100),
      uk_cityplay_m: Math.round((salesGroups.uk_cityplay * (mlSalesSplit.uk_cityplay?.m || 50)) / 100),
      uk_cityplay_l: Math.round((salesGroups.uk_cityplay * (mlSalesSplit.uk_cityplay?.l || 50)) / 100),
      uk_20_m: Math.round((salesGroups.uk_20 * (mlSalesSplit.uk_20?.m || 50)) / 100),
      uk_20_l: Math.round((salesGroups.uk_20 * (mlSalesSplit.uk_20?.l || 50)) / 100),
    };

// === Amazon split ===
const amazonObj = percentages.amazon_share || {};
const AMAZON_TOTAL = amazonObj.total ?? 0.25;        // 25% מהטארגט
const amazonSplit = amazonObj.split || {};

function getAmazonSplit(region, product, size) {
  try {
    const regionData = amazonSplit?.[region];
    if (!regionData) return 0;

    const base = regionData[product]?.[size] ?? 0;   // למשל 0.33, 0.51, 0.08...
    const regionWeight = regionData.total ?? 0;      // us: 0.75, uk: 0.15

    const share = AMAZON_TOTAL * regionWeight * base;

    return share;
  } catch (err) {
    console.error("❌ getAmazonSplit error:", err);
    return 0;
  }
}

// =====================================
// 🧮 Accurate Regular vs Amazon Split
// =====================================
const regularSales = {};
const amazonSales = {};

const REGULAR_FACTOR = 1 - AMAZON_TOTAL;  // 0.75

Object.keys(salesML).forEach((k) => {
  const [region, rawProduct, size] = k.split("_");
  const product = rawProduct === "20" ? "2_0" : rawProduct;

  const amazonShare = getAmazonSplit(region, product, size);

  amazonSales[k] = Math.round(totalSales * amazonShare);

  regularSales[k] = Math.round(salesML[k] * REGULAR_FACTOR);
});

const totalRegular = Object.values(regularSales).reduce((a, b) => a + b, 0);
const totalAmazon  = Object.values(amazonSales).reduce((a, b) => a + b, 0);



    // === Apply sales ===
    const nextRunning = { ...running };
    const nextAmazon = { ...runningAmazon };

    Object.keys(salesML).forEach((k) => {
      nextRunning[k] = (nextRunning[k] || 0) - (regularSales[k] || 0);
      nextAmazon[k] = (nextAmazon[k] || 0) - (amazonSales[k] || 0);
    });

    // === Apply production ===
    Object.keys(prodML).forEach((k) => {
      const [region, rawProduct, size] = k.split("_");
      const product = rawProduct === "20" ? "2_0" : rawProduct;
      const baseSplit = getAmazonSplit(region, product, size);
      const effectiveShare = baseSplit > 0 ? baseSplit : AMAZON_TOTAL;
      const addedRegular = Math.round(prodML[k] * (1 - effectiveShare));
      const addedAmazon = Math.round(prodML[k] * effectiveShare);
      nextRunning[k] = (nextRunning[k] || 0) + addedRegular;
      nextAmazon[k] = (nextAmazon[k] || 0) + addedAmazon;
    });

    // === Manual stock overrides ===
    const manual = manualStockOverrides?.[day.date];
    if (manual) {
      Object.entries(manual).forEach(([key, val]) => {
        if (typeof val !== "number") return;
        if (key.startsWith("us_amazon_") || key.startsWith("uk_amazon_")) {
          const cleanKey = key.replace(/^us_amazon_/, "us_").replace(/^uk_amazon_/, "uk_");
          nextAmazon[cleanKey] = (nextAmazon[cleanKey] || 0) + val;
        } else {
          nextRunning[key] = (nextRunning[key] || 0) + val;
        }
      });
    }

    // === Production overrides (apply only once) ===
    const prodOverride = adjustedOverrides?.[day.date];
    if (prodOverride) {
      if (!day.forecast) day.forecast = {};
      if (!day.forecast.production) day.forecast.production = {};
      Object.entries(prodOverride).forEach(([sku, qty]) => {
        day.forecast.production[sku] = qty;
        if (sku.startsWith("amazon_")) {
          const cleanKey = sku.replace(/^amazon_/, "");
        } else {
        }
      });
    }

    const total_stock =
      Object.values(nextRunning).reduce((a, b) => a + b, 0) +
      Object.values(nextAmazon).reduce((a, b) => a + b, 0);

    running = { ...nextRunning };
    runningAmazon = { ...nextAmazon };

    return {
      ...day,
      forecast: {
        ...day.forecast,
        sales: regularSales,
        sales_amazon: amazonSales,
      },
      stock: {
        ...nextRunning,
        amazon: nextAmazon,
        total_stock: Math.round(total_stock),
      },
    };
  });

  return simulated.filter((d) => d && d.date >= baseDayData.date);
}, [allData, baseDayData, percentages, manualStockOverrides]);


// ===================================================
// 🧱 WarehouseStockCards Component
// Shows stock breakdown per warehouse + depletion dates
// ===================================================
const WarehouseStockCards = ({ data, stats }) => {

  // ===================================================
  // 🧮 Function: Find when each SKU stock runs out (ITB & Amazon)
  // ===================================================
const findDepletionDates = (filteredData) => {

  if (!Array.isArray(filteredData) || filteredData.length === 0)
    return { itb: {}, amazon: {} };

  const todayISO = new Date().toISOString().split("T")[0];
  const SKU_KEYS = [
    "us_cityplay_m", "us_cityplay_l", "us_20_m", "us_20_l",
    "uk_cityplay_m", "uk_cityplay_l", "uk_20_m", "uk_20_l",
  ];

  const depletionMap = { itb: {}, amazon: {} };

  SKU_KEYS.forEach((key) => {
    const series = filteredData
      .filter((d) => d.date >= todayISO)
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .map((d) => ({
        date: d.date,
        regular: d.stock?.[key] ?? 0,
        amazon: d.stock?.amazon?.[key] ?? 0,
      }));

    if (series.length === 0) return;

    let cumulativeRegular = series[0].regular;
    let cumulativeAmazon = series[0].amazon;
    let depletionItb = null;
    let depletionAmazon = null;

    for (let i = 1; i < series.length; i++) {
      const curr = series[i];
      const prev = series[i - 1];
      const deltaRegular = curr.regular - prev.regular;
      const deltaAmazon = curr.amazon - prev.amazon;
      cumulativeRegular += deltaRegular;
      cumulativeAmazon += deltaAmazon;
      if (!depletionItb && cumulativeRegular <= 0) depletionItb = curr.date;
      if (!depletionAmazon && cumulativeAmazon <= 0) depletionAmazon = curr.date;
      if (depletionItb && depletionAmazon) break;
    }

    depletionMap.itb[key] = depletionItb ? `ITB (${depletionItb})` : "∞";
    depletionMap.amazon[key] = depletionAmazon ? `Amazon (${depletionAmazon})` : "∞";
  });

  return depletionMap;
};


  // ===================================================
  // 🧮 Calculate depletion using the simulated data (filteredData)
  // ===================================================
  const depletion = findDepletionDates(data);

  // Extract warehouse breakdown from stats
  const safeBreakdown = stats?.warehouseBreakdown || {};

  // Map region + warehouse names to data blocks
  const warehouses = {
    "🇺🇸 US — ITB": safeBreakdown?.US?.ITB,
    "🇺🇸 US — Amazon": safeBreakdown?.US?.Amazon,
    "🇬🇧 UK — Tive": safeBreakdown?.EU?.Tive,
    "🇬🇧 UK — Amazon": safeBreakdown?.EU?.Amazon,
  };

  // ===================================================
  // 🧱 Render: Warehouse cards grid
  // ===================================================
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
        gap: "1rem",
        marginBottom: "1.5rem",
      }}
    >
      {Object.entries(warehouses).map(([title, wh]) => {
        if (!wh) return null;
        const isAmazon = title.includes("Amazon");

        // Choose the correct depletion source
        const depMap = isAmazon ? depletion.amazon : depletion.itb;

        // Define prefix for mapping depletion keys correctly
        // (US → us, EU → uk)
        const prefix = title.includes("US") ? "us" : "uk";

        return (
          <div
            key={title}
            style={{
              background: "white",
              borderRadius: 10,
              border: "1px solid #e5e7eb",
              boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
              padding: "1rem",
              fontSize: "0.85rem",
            }}
          >
            {/* 🏷️ Warehouse Title */}
            <div
              style={{
                fontWeight: 700,
                marginBottom: "0.4rem",
                fontSize: "0.95rem",
              }}
            >
              {title}
            </div>

            {/* 🏗️ Product group: CityPlay */}
            <div style={{ fontWeight: 600 }}>CityPlay</div>
            <WarehouseMiniLine
              label="M"
              value={wh.cityplay?.M}
              depletion={depMap[`${prefix}_cityplay_m`]}
              title={title}
            />
            <WarehouseMiniLine
              label="L"
              value={wh.cityplay?.L}
              depletion={depMap[`${prefix}_cityplay_l`]}
              title={title}
            />

            {/* 🏗️ Product group: 2.0 */}
            <div style={{ fontWeight: 600, marginTop: "0.4rem" }}>2.0</div>
            <WarehouseMiniLine
              label="M"
              value={wh.two_point_0?.M}
              depletion={depMap[`${prefix}_20_m`]}
              title={title}
            />
            <WarehouseMiniLine
              label="L"
              value={wh.two_point_0?.L}
              depletion={depMap[`${prefix}_20_l`]}
              title={title}
            />
          </div>
        );
      })}
    </div>
  );
};


// ===================================================
// ✅ Small row component (M/L + Depletion Date) — centered value
// ===================================================
const WarehouseMiniLine = ({ label, value, depletion, title }) => {
  const match = depletion?.match(/\d{4}-\d{2}-\d{2}/);
  const formatted =
    match && match[0]
      ? new Date(match[0]).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
        })
      : null;

  const source = (() => {
    if (!depletion) return "";
    let text = depletion.replace(/\(.*\)/, "").trim();

    // ✅ Swap only based on region
    if (title?.includes("EU")) {
      if (text.includes("ITB")) text = text.replace("ITB", "TIVE");
    } else if (title?.includes("US")) {
      if (text.includes("TIVE")) text = text.replace("TIVE", "ITB");
    }

    return text;
  })();

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr auto 1fr",
        alignItems: "center",
        justifyContent: "center",
        marginTop: "0.2rem",
        gap: "0.4rem",
        textAlign: "center",
      }}
    >
      <span style={{ textAlign: "left", fontWeight: 500 }}>{label}:</span>
      <strong style={{ fontWeight: 700, fontSize: "0.9rem" }}>
        {(value ?? 0).toLocaleString()}
      </strong>
      <span
        style={{
          fontSize: "0.75rem",
          color: "#6b7280",
          textAlign: "right",
          whiteSpace: "nowrap",
        }}
      >
        ⏳ {formatted || depletion || "∞"}
      </span>
    </div>
  );
};


const InventoryStatusCards = ({ data }) => {
  if (!data || data.length === 0) return null;

  const todayISO = new Date().toISOString().split("T")[0];

  const SKU_KEYS = [
    "us_cityplay_m",
    "us_cityplay_l",
    "us_20_m",
    "us_20_l",
    "uk_cityplay_m",
    "uk_cityplay_l",
    "uk_20_m",
    "uk_20_l",
  ];

  const LABELS = {
    us_cityplay_m: "🇺🇸 Cityplay M",
    us_cityplay_l: "🇺🇸 Cityplay L",
    us_20_m: "🇺🇸 2.0 M",
    us_20_l: "🇺🇸 2.0 L",
    uk_cityplay_m: "🇬🇧 Cityplay M",
    uk_cityplay_l: "🇬🇧 Cityplay L",
    uk_20_m: "🇬🇧 2.0 M",
    uk_20_l: "🇬🇧 2.0 L",
  };

  const cards = SKU_KEYS.map((key) => {
    const stockSeries = data
      .filter((d) => d.date >= todayISO)
      .map((d) => ({
        date: d.date,
        regular: d.stock?.[key] ?? 0,
        amazon: d.stock?.amazon?.[key] ?? 0,
      }));

    const todayStock = stockSeries[0] || { regular: 0, amazon: 0 };
    const totalToday = (todayStock.regular || 0) + (todayStock.amazon || 0);

    // 🔍 Find depletion points for each source
    const regularDepletion = stockSeries.find((s) => s.regular <= 0);
    const amazonDepletion = stockSeries.find((s) => s.amazon <= 0);
    const bothDepletion = stockSeries.find((s) => s.regular + s.amazon <= 0);

    let depletionLabel = "∞";
    if (bothDepletion) depletionLabel = `All (${bothDepletion.date})`;
    else if (regularDepletion) depletionLabel = `ITB (${regularDepletion.date})`;
    else if (amazonDepletion) depletionLabel = `Amazon (${amazonDepletion.date})`;

    // 🎨 Color logic
    let bg = "#ecfdf5";
    let text = "#065f46";
    if (totalToday < 50 && totalToday > 0) {
      bg = "#fef3c7";
      text = "#92400e";
    } else if (totalToday <= 0) {
      bg = "#fee2e2";
      text = "#991b1b";
    }

    return {
      key,
      label: LABELS[key],
      regular: todayStock.regular,
      amazon: todayStock.amazon,
      total: totalToday,
      depletionLabel,
      bg,
      text,
    };
  });

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
        gap: "0.6rem",
        marginBottom: "1rem",
      }}
    >
      {cards.map((c) => (
        <div
          key={c.key}
          style={{
            background: c.bg,
            border: `1px solid ${c.text}`,
            borderRadius: "6px",
            padding: "0.5rem",
            textAlign: "center",
            color: c.text,
            fontWeight: 600,
            fontSize: "0.8rem",
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
          }}
        >
          <div style={{ fontSize: "0.85rem", marginBottom: "0.1rem" }}>{c.label}</div>
          <div style={{ fontSize: "0.9rem" }}>
            🏢 {c.regular} <span style={{ opacity: 0.7 }}>+ 🛒 {c.amazon}</span>
          </div>
          <div style={{ fontSize: "0.75rem", opacity: 0.8 }}>
            Total: {c.total}
          </div>
          <div style={{ fontSize: "0.7rem", marginTop: "0.1rem" }}>
            Ends:{" "}
            {c.depletionLabel === "∞"
              ? "∞"
              : new Date(c.depletionLabel.match(/\d{4}-\d{2}-\d{2}/)?.[0] || "").toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "short",
                }) +
                " " +
                c.depletionLabel.replace(/\(.*\)/, "").trim()}
          </div>
        </div>
      ))}
    </div>
  );
};

  // ==============================
  // 📦 Data logic
  // ==============================
  if (loading) return <LoadingView />;
  if (!loading && allData.length === 0 && !error)
    return <EmptyView onAddFirstDay={onAddFirstDay} />;
  if (!baseDayData)
    return (
      <div style={{ padding: 20, textAlign: "center" }}>
        ⏳ Loading yesterday’s base inventory...
      </div>
    );

  const handleDelete = (day) => {
    if (
      window.confirm(
        `Delete data for ${new Date(day.date).toLocaleDateString("en-US", {
          month: "short",
          day: "2-digit",
        })}?`
      )
    )
      onDeleteDay(day);
  };


// ===================================================
// 📊 Q4 Summary — include future target in Q4ToDateSales
// ===================================================
const Q4Summary = ({ allData, filteredData, manualStockOverrides }) => {
  if (!Array.isArray(allData) || allData.length === 0) return null;

  const now = new Date();
  const q4Start = new Date("2025-10-01");
  const q4End = new Date("2025-12-31");

  const q4Data = allData.filter((d) => {
    const day = new Date(d.date);
    return day >= q4Start && day <= q4End;
  });

  if (q4Data.length === 0) return null;

  let q4ToDateSales = 0;
  let totalProductionPlan = 0;

  q4Data.forEach((d) => {
    const dayDate = new Date(d.date);

    const prodTotal =
      (d.manufacturing?.plan_target_total || 0) +
      Object.values(d.forecast?.production || {}).reduce((a, b) => a + (b || 0), 0);

    if (dayDate <= now) {
      q4ToDateSales += d.sales?.target_total || 0;
    } else {
      const forecastSales = Object.values(d.forecast?.sales || {}).reduce(
        (a, b) => a + (b || 0),
        0
      );
      q4ToDateSales += forecastSales;
    }

    totalProductionPlan += prodTotal;

    // ✅ Add manualStockOverrides (by date)
    const manualForDay = manualStockOverrides?.[d.date];
    if (manualForDay) {
      Object.values(manualForDay).forEach((v) => {
        if (typeof v === "number") totalProductionPlan += v;
      });
    }
  });

  // 📦 Predicted Stock
  let predictedStock = 0;
  if (Array.isArray(filteredData) && filteredData.length > 0) {
    const lastDay = filteredData[filteredData.length - 1];
    predictedStock = lastDay?.stock?.total_stock ?? 0;
  }

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
        gap: "1rem",
        marginBottom: "1.5rem",
        position: "relative",
      }}
    >
      <SummaryCard
        title="📅 Q4 Actual + Future Target"
        value={q4ToDateSales}
        color="#0284c7"
      />
      <SummaryCard
        title="🏭 Q4 Total Production"
        value={totalProductionPlan}
        color="#d97706"
      />
      <SummaryCard
        title="📦 Q4 Predicted Stock"
        value={predictedStock}
        color="#4f46e5"
      />
    </div>
  );
};




// 🔹 Simple KPI Card component
const SummaryCard = ({ title, value, color }) => (
  <div
    style={{
      background: "white",
      border: "1px solid #e5e7eb",
      borderRadius: "8px",
      padding: "1rem",
      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
      textAlign: "center",
    }}
  >
    <div style={{ fontSize: "1.4rem", fontWeight: 700, color }}>
      {Math.round(value).toLocaleString()}
    </div>
    <div style={{ fontSize: "0.85rem", color: "#6b7280" }}>{title}</div>
  </div>
);




  // ==============================
  // 🧮 Render
  // ==============================
  return (
      <div style={containerStyle}>

        {/* 📊 Q4 Summary Section */}
      <Q4Summary allData={allData} filteredData={filteredData} manualStockOverrides={manualStockOverrides} />

{/* 🚚 Factory Shipments In Transit */}
{inTransit.length > 0 && (
  <div
    style={{
      background: "#fff7ed",
      border: "1px solid #fed7aa",
      borderRadius: 8,
      padding: "1rem",
      marginBottom: "1.5rem",
    }}
  >
    <h4 style={{ margin: 0, fontWeight: 700, color: "#92400e" }}>
      🚚 Shipments In Transit ({inTransit.length})
    </h4>

    <div
      style={{
        maxHeight: "300px",
        overflowY: "auto",
        marginTop: "0.5rem",
        borderTop: "1px solid #fed7aa",
      }}
    >
      <table style={{ width: "100%", fontSize: "0.85rem" }}>
        <thead>
          <tr style={{ textAlign: "left", background: "#fffbeb" }}>
            <th style={{ padding: "0.5rem" }}>📦 SKU</th>
            <th style={{ padding: "0.5rem" }}>🏭 From Factory</th>
            <th style={{ padding: "0.5rem" }}>🏢 To Warehouse</th>
            <th style={{ padding: "0.5rem" }}>📦 Quantity</th>
            <th style={{ padding: "0.5rem" }}>🚢 Ship Date</th>
            <th style={{ padding: "0.5rem" }}>📆 Days in Transit</th>
            <th style={{ padding: "0.5rem" }}>🚛 Shipping Company</th>
            <th style={{ padding: "0.5rem" }}>🔗 Tracking</th>
            <th style={{ padding: "0.5rem" }}>📍 Location</th>

          </tr>
        </thead>
        <tbody>
          {inTransit.map((s) => (
            <tr key={s.id}>
              <td style={{ padding: "0.5rem" }}>{s.sku}</td>
              <td style={{ padding: "0.5rem" }}>{s.from_factory}</td>
              <td style={{ padding: "0.5rem" }}>{s.to_warehouse}</td>
              <td style={{ padding: "0.5rem", textAlign: "center" }}>
                {s.quantity && Number(s.quantity) > 0
                  ? s.quantity
                  : s.serial_box
                  ? 1
                  : "-"}
              </td>
              <td style={{ padding: "0.5rem" }}>
                {s.ship_date
                  ? new Date(s.ship_date).toLocaleDateString("en-GB")
                  : "-"}
              </td>
              <td style={{ padding: "0.5rem" }}>
                {typeof s.days_since_ship === "number"
                  ? s.days_since_ship
                  : "-"}
              </td>
              <td style={{ padding: "0.5rem" }}>
                {s.shipping_company || "-"}
              </td>
<td style={{ padding: "0.5rem" }}>
  {s.tracking_number ? (
    <a
      href={`https://www.google.com/search?q=${encodeURIComponent(
        s.tracking_number
      )}`}
      target="_blank"
      rel="noopener noreferrer"
    >
      {s.tracking_number}
    </a>
  ) : (
    "-"
  )}
</td>

<td style={{ padding: "0.5rem" }}>
  {s.current_location ? (
    <a
      href={`https://parcelsapp.com/en/tracking/${encodeURIComponent(s.tracking_number)}`}
      target="_blank"
      rel="noopener noreferrer"
    >
      {s.current_location}
    </a>
  ) : s.tracking_number ? (
    <a
      href={`https://parcelsapp.com/en/tracking/${encodeURIComponent(s.tracking_number)}`}
      target="_blank"
      rel="noopener noreferrer"
    >
      Track package
    </a>
  ) : (
    "-"
  )}
</td>


            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
)}



      {/* 📦 Quick Inventory Status (Top Summary Cards) */}
      <WarehouseStockCards stats={stats} data={filteredData} />

      <div style={stickyHeader}>
        📋 Planning Data Overview ({filteredData.length} days)
        <button onClick={() => setShowConfig(!showConfig)} style={settingsBtn}>
          <Settings size={18} color="#374151" />
        </button>

        {showConfig && (
          <div style={settingsMenu}>
            {Object.entries(sectionLabels).map(([key, label]) => (
              <label key={key} style={{ display: "block", marginBottom: 6 }}>
                <input
                  type="checkbox"
                  checked={visibleSections[key]}
                  onChange={(e) =>
                    setVisibleSections((prev) => ({
                      ...prev,
                      [key]: e.target.checked,
                    }))
                  }
                />{" "}
                {label}
              </label>
            ))}

            <hr style={{ margin: "0.75rem 0" }} />

            <button
              style={{
                background: "#2563eb",
                color: "white",
                border: "none",
                borderRadius: "6px",
                padding: "0.5rem 1rem",
                cursor: "pointer",
                fontWeight: 600,
              }}
              onClick={() => setShowPercentagesModal(true)}
            >
              ⚙️ Adjust % Splits
            </button>
          </div>
        )}

        {showPercentagesModal && (
          <AdjustPercentagesModal
            onClose={() => setShowPercentagesModal(false)}
            onRefresh={() => setRefreshTrigger((x) => x + 1)}
          />
        )}
      </div>


      <div
        style={{
          maxHeight: "80vh",
          overflowY: "auto",
          overflowX: "auto",
          position: "relative",
        }}
      >
        <table style={tableStyle}>
          <thead style={{ position: "sticky", top: 0, zIndex: 100 }}>
            <tr style={{ background: "#f8fafc" }}>
              <th style={stickyTh}>📅 Date</th>

              {visibleSections.salesForecast && (
                <th colSpan={17} style={thGroup("#ecfdf5", "#065f46")}>
                  📈 Sales Forecast Plan
                </th>
              )}
              {visibleSections.actualSales && (
                <th colSpan={5} style={thGroup("#f0f9ff", "#0c4a6e")}>
                  📊 Actual Sales
                </th>
              )}
              {visibleSections.productionPlan && (
                <th colSpan={16} style={thGroup("#fffbeb", "#92400e")}>
                  🏭 Production Plan
                </th>
              )}
              {visibleSections.actualMFG && (
                <th colSpan={2} style={thGroup("#fff7ed", "#78350f")}>
                  Actual MFG
                </th>
              )}
              {visibleSections.stock && (
                <th colSpan={17} style={thGroup("#eef2ff", "#3730a3")}>
                  📦 Stock (Updated with Real Arrival Logic)
                </th>
              )}
              {visibleSections.shipment && (
                <th colSpan={4} style={thGroup("#fdf2f8", "#9d174d")}>
                  🚚 Shipment
                </th>
              )}
              {visibleSections.duration && (
                <th colSpan={4} style={thGroup("#fff1f2", "#b91c1c")}>
                  🕒 Shipment Duration (Days)
                </th>
              )}
              <th style={actionTh}>Actions</th>
            </tr>

            {/* ==== SUB HEADERS ==== */}
            <tr style={{ background: "#f9fafb" }}>
              <th style={stickyTh}></th>

              {/* Forecast */}
            {visibleSections.salesForecast && (
              <>
                <th style={colBg("#ecfdf5")}>🎯 Target</th>

                {/* 🇺🇸 US Cityplay */}
                <th style={colBg("#ecfdf5")}>🇺🇸 Cityplay L</th>
                <th style={colBg("#ecfdf5")}>🇺🇸 Cityplay L (Amazon)</th>
                <th style={colBg("#ecfdf5")}>🇺🇸 Cityplay M</th>
                <th style={colBg("#ecfdf5")}>🇺🇸 Cityplay M (Amazon)</th>

                {/* 🇺🇸 US 2.0 */}
                <th style={colBg("#ecfdf5")}>🇺🇸 2.0 L</th>
                <th style={colBg("#ecfdf5")}>🇺🇸 2.0 L (Amazon)</th>
                <th style={colBg("#ecfdf5")}>🇺🇸 2.0 M</th>
                <th style={colBg("#ecfdf5")}>🇺🇸 2.0 M (Amazon)</th>

                {/* 🇬🇧 UK Cityplay */}
                <th style={colBg("#ecfdf5")}>🇬🇧 Cityplay L</th>
                <th style={colBg("#ecfdf5")}>🇬🇧 Cityplay L (Amazon)</th>
                <th style={colBg("#ecfdf5")}>🇬🇧 Cityplay M</th>
                <th style={colBg("#ecfdf5")}>🇬🇧 Cityplay M (Amazon)</th>

                {/* 🇬🇧 UK 2.0 */}
                <th style={colBg("#ecfdf5")}>🇬🇧 2.0 L</th>
                <th style={colBg("#ecfdf5")}>🇬🇧 2.0 L (Amazon)</th>
                <th style={colBg("#ecfdf5")}>🇬🇧 2.0 M</th>
                <th style={colBg("#ecfdf5")}>🇬🇧 2.0 M (Amazon)</th>
              </>
            )}


              {/* Actual Sales */}
              {visibleSections.actualSales && (
                <>
                  <th style={colBg("#f0f9ff")}>Total</th>
                  <th style={colBg("#f0f9ff")}>🇺🇸 US</th>
                  <th style={colBg("#f0f9ff")}>🇪🇺 EU</th>
                  <th style={colBg("#f0f9ff")}>Cityplay</th>
                  <th style={colBg("#f0f9ff")}>2.0</th>
                </>
              )}

              {/* Production */}
              {visibleSections.productionPlan && (
                <>
                  {/* 🇺🇸 ITB */}
                  <th style={colBg("#fffbeb")}>🇺🇸 ITB Cityplay M</th>
                  <th style={colBg("#fffbeb")}>🇺🇸 ITB Cityplay L</th>
                  <th style={colBg("#fffbeb")}>🇺🇸 ITB 2.0 M</th>
                  <th style={colBg("#fffbeb")}>🇺🇸 ITB 2.0 L</th>

                  {/* 🇺🇸 Amazon */}
                  <th style={colBg("#fffbeb")}>🇺🇸 Amazon Cityplay M</th>
                  <th style={colBg("#fffbeb")}>🇺🇸 Amazon Cityplay L</th>
                  <th style={colBg("#fffbeb")}>🇺🇸 Amazon 2.0 M</th>
                  <th style={colBg("#fffbeb")}>🇺🇸 Amazon 2.0 L</th>

                  {/* 🇬🇧 TIVE */}
                  <th style={colBg("#fffbeb")}>🇬🇧 TIVE Cityplay M</th>
                  <th style={colBg("#fffbeb")}>🇬🇧 TIVE Cityplay L</th>
                  <th style={colBg("#fffbeb")}>🇬🇧 TIVE 2.0 M</th>
                  <th style={colBg("#fffbeb")}>🇬🇧 TIVE 2.0 L</th>

                  {/* 🇬🇧 Amazon UK */}
                  <th style={colBg("#fffbeb")}>🇬🇧 Amazon Cityplay M</th>
                  <th style={colBg("#fffbeb")}>🇬🇧 Amazon Cityplay L</th>
                  <th style={colBg("#fffbeb")}>🇬🇧 Amazon 2.0 M</th>
                  <th style={colBg("#fffbeb")}>🇬🇧 Amazon 2.0 L</th>
                </>
              )}


              {/* Actual MFG */}
              {visibleSections.actualMFG && (
                <>
                  <th style={colBg("#fff7ed")}>🇺🇸</th>
                  <th style={colBg("#fff7ed")}>🇪🇺</th>
                </>
              )}

              {/* Stock */}
              {visibleSections.stock && (
                <>
                  {/* 🇺🇸 US Cityplay */}
                  <th style={colBg("#eef2ff")}>🇺🇸 Cityplay M (ITB)</th>
                  <th style={colBg("#eef2ff")}>🇺🇸 Cityplay M (Amazon)</th>
                  <th style={colBg("#eef2ff")}>🇺🇸 Cityplay L (ITB)</th>
                  <th style={colBg("#eef2ff")}>🇺🇸 Cityplay L (Amazon)</th>

                  {/* 🇺🇸 US 2.0 */}
                  <th style={colBg("#eef2ff")}>🇺🇸 2.0 M (ITB)</th>
                  <th style={colBg("#eef2ff")}>🇺🇸 2.0 M (Amazon)</th>
                  <th style={colBg("#eef2ff")}>🇺🇸 2.0 L (ITB)</th>
                  <th style={colBg("#eef2ff")}>🇺🇸 2.0 L (Amazon)</th>

                  {/* 🇬🇧 UK Cityplay */}
                  <th style={colBg("#eef2ff")}>🇬🇧 Cityplay M (TIVE)</th>
                  <th style={colBg("#eef2ff")}>🇬🇧 Cityplay M (Amazon)</th>
                  <th style={colBg("#eef2ff")}>🇬🇧 Cityplay L (TIVE)</th>
                  <th style={colBg("#eef2ff")}>🇬🇧 Cityplay L (Amazon)</th>

                  {/* 🇬🇧 UK 2.0 */}
                  <th style={colBg("#eef2ff")}>🇬🇧 2.0 M (TIVE)</th>
                  <th style={colBg("#eef2ff")}>🇬🇧 2.0 M (Amazon)</th>
                  <th style={colBg("#eef2ff")}>🇬🇧 2.0 L (TIVE)</th>
                  <th style={colBg("#eef2ff")}>🇬🇧 2.0 L (Amazon)</th>

                  {/* Total */}
                  <th style={colBg("#dcfce7")}>📦 Total</th>
                </>
              )}


              {/* Shipment */}
              {visibleSections.shipment && (
                <>
                  <th style={colBg("#fdf2f8")}>US Days</th>
                  <th style={colBg("#fdf2f8")}>US Date</th>
                  <th style={colBg("#fdf2f8")}>EU Days</th>
                  <th style={colBg("#fdf2f8")}>EU Date</th>
                </>
              )}

              {/* Duration */}
              {visibleSections.duration && (
                <>
                  <th style={colBg("#fff1f2")}>🇨🇳→🇺🇸</th>
                  <th style={colBg("#fff1f2")}>🇨🇳→🇪🇺</th>
                  <th style={colBg("#fff1f2")}>🇺🇸→👥</th>
                  <th style={colBg("#fff1f2")}>🇪🇺→👥</th>
                </>
              )}
              <th></th>
            </tr>
          </thead>

          {/* ==== BODY ==== */}
          <tbody>
            {filteredData.map((day, i) => (
              <tr key={day.date} style={{ background: i % 2 ? "#fafafa" : "white" }}>
                <td style={stickyTd(i)}>
                  {new Date(day.date).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  })}
                </td>

                {/* Forecast */}
                {visibleSections.salesForecast && (
                  <>
                    <td style={colBg("#ecfdf5")}>{Math.round(day.sales?.target_total ?? 0)}</td>

                    {/* 🇺🇸 Cityplay */}
                    <td style={colBg("#ecfdf5")}>{Math.round(day.forecast?.sales?.us_cityplay_l ?? 0)}</td>
                    <td style={colBg("#ecfdf5")}>{Math.round(day.forecast?.sales_amazon?.us_cityplay_l ?? 0)}</td>
                    <td style={colBg("#ecfdf5")}>{Math.round(day.forecast?.sales?.us_cityplay_m ?? 0)}</td>
                    <td style={colBg("#ecfdf5")}>{Math.round(day.forecast?.sales_amazon?.us_cityplay_m ?? 0)}</td>

                    {/* 🇺🇸 2.0 */}
                    <td style={colBg("#ecfdf5")}>{Math.round(day.forecast?.sales?.us_20_l ?? 0)}</td>
                    <td style={colBg("#ecfdf5")}>{Math.round(day.forecast?.sales_amazon?.us_20_l ?? 0)}</td>
                    <td style={colBg("#ecfdf5")}>{Math.round(day.forecast?.sales?.us_20_m ?? 0)}</td>
                    <td style={colBg("#ecfdf5")}>{Math.round(day.forecast?.sales_amazon?.us_20_m ?? 0)}</td>

                    {/* 🇬🇧 Cityplay */}
                    <td style={colBg("#ecfdf5")}>{Math.round(day.forecast?.sales?.uk_cityplay_l ?? 0)}</td>
                    <td style={colBg("#ecfdf5")}>{Math.round(day.forecast?.sales_amazon?.uk_cityplay_l ?? 0)}</td>
                    <td style={colBg("#ecfdf5")}>{Math.round(day.forecast?.sales?.uk_cityplay_m ?? 0)}</td>
                    <td style={colBg("#ecfdf5")}>{Math.round(day.forecast?.sales_amazon?.uk_cityplay_m ?? 0)}</td>

                    {/* 🇬🇧 2.0 */}
                    <td style={colBg("#ecfdf5")}>{Math.round(day.forecast?.sales?.uk_20_l ?? 0)}</td>
                    <td style={colBg("#ecfdf5")}>{Math.round(day.forecast?.sales_amazon?.uk_20_l ?? 0)}</td>
                    <td style={colBg("#ecfdf5")}>{Math.round(day.forecast?.sales?.uk_20_m ?? 0)}</td>
                    <td style={colBg("#ecfdf5")}>{Math.round(day.forecast?.sales_amazon?.uk_20_m ?? 0)}</td>
                  </>
                )}



                {/* Actual Sales */}
                {visibleSections.actualSales && (
                  <>
                    <td style={colBg("#f0f9ff")}>
                      {day.sales?.actual_total ?? "-"}
                    </td>
                    <td style={colBg("#f0f9ff")}>
                      {day.sales?.actual_us ?? "-"}
                    </td>
                    <td style={colBg("#f0f9ff")}>
                      {day.sales?.actual_eu ?? "-"}
                    </td>
                    <td style={colBg("#f0f9ff")}>
                      {day.sales?.cityplay_actual ?? "-"}
                    </td>
                    <td style={colBg("#f0f9ff")}>
                      {day.sales?.two_point_0_actual ?? "-"}
                    </td>
                  </>
                )}

              {/* Production */}
              {visibleSections.productionPlan && (
                <>
                  {/* 🇺🇸 ITB */}
                  <td style={colBg("#fffbeb")}>{Math.round(day.forecast?.production?.us_cityplay_m ?? 0)}</td>
                  <td style={colBg("#fffbeb")}>{Math.round(day.forecast?.production?.us_cityplay_l ?? 0)}</td>
                  <td style={colBg("#fffbeb")}>{Math.round(day.forecast?.production?.us_20_m ?? 0)}</td>
                  <td style={colBg("#fffbeb")}>{Math.round(day.forecast?.production?.us_20_l ?? 0)}</td>

                  {/* 🇺🇸 Amazon */}
                  <td style={colBg("#fffbeb")}>{Math.round(day.forecast?.production?.amazon_us_cityplay_m ?? 0)}</td>
                  <td style={colBg("#fffbeb")}>{Math.round(day.forecast?.production?.amazon_us_cityplay_l ?? 0)}</td>
                  <td style={colBg("#fffbeb")}>{Math.round(day.forecast?.production?.amazon_us_20_m ?? 0)}</td>
                  <td style={colBg("#fffbeb")}>{Math.round(day.forecast?.production?.amazon_us_20_l ?? 0)}</td>

                  {/* 🇬🇧 TIVE */}
                  <td style={colBg("#fffbeb")}>{Math.round(day.forecast?.production?.uk_cityplay_m ?? 0)}</td>
                  <td style={colBg("#fffbeb")}>{Math.round(day.forecast?.production?.uk_cityplay_l ?? 0)}</td>
                  <td style={colBg("#fffbeb")}>{Math.round(day.forecast?.production?.uk_20_m ?? 0)}</td>
                  <td style={colBg("#fffbeb")}>{Math.round(day.forecast?.production?.uk_20_l ?? 0)}</td>

                  {/* 🇬🇧 Amazon UK */}
                  <td style={colBg("#fffbeb")}>{Math.round(day.forecast?.production?.amazon_uk_cityplay_m ?? 0)}</td>
                  <td style={colBg("#fffbeb")}>{Math.round(day.forecast?.production?.amazon_uk_cityplay_l ?? 0)}</td>
                  <td style={colBg("#fffbeb")}>{Math.round(day.forecast?.production?.amazon_uk_20_m ?? 0)}</td>
                  <td style={colBg("#fffbeb")}>{Math.round(day.forecast?.production?.amazon_uk_20_l ?? 0)}</td>
                </>
              )}



                {/* Actual MFG */}
                {visibleSections.actualMFG && (
                  <>
                    <td style={colBg("#fff7ed")}>
                      {day.manufacturing?.actual_us ?? "-"}
                    </td>
                    <td style={colBg("#fff7ed")}>
                      {day.manufacturing?.actual_eu ?? "-"}
                    </td>
                  </>
                )}

              {/* Stock */}
              {visibleSections.stock && (
                <>
                  {/* 🇺🇸 US Cityplay */}
                  <td style={colBg("#eef2ff")}>{Math.round(day.stock?.us_cityplay_m ?? 0)}</td>
                  <td style={colBg("#eef2ff")}>{Math.round(day.stock?.amazon?.us_cityplay_m ?? 0)}</td>
                  <td style={colBg("#eef2ff")}>{Math.round(day.stock?.us_cityplay_l ?? 0)}</td>
                  <td style={colBg("#eef2ff")}>{Math.round(day.stock?.amazon?.us_cityplay_l ?? 0)}</td>

                  {/* 🇺🇸 US 2.0 */}
                  <td style={colBg("#eef2ff")}>{Math.round(day.stock?.us_20_m ?? 0)}</td>
                  <td style={colBg("#eef2ff")}>{Math.round(day.stock?.amazon?.us_20_m ?? 0)}</td>
                  <td style={colBg("#eef2ff")}>{Math.round(day.stock?.us_20_l ?? 0)}</td>
                  <td style={colBg("#eef2ff")}>{Math.round(day.stock?.amazon?.us_20_l ?? 0)}</td>

                  {/* 🇬🇧 UK Cityplay */}
                  <td style={colBg("#eef2ff")}>{Math.round(day.stock?.uk_cityplay_m ?? 0)}</td>
                  <td style={colBg("#eef2ff")}>{Math.round(day.stock?.amazon?.uk_cityplay_m ?? 0)}</td>
                  <td style={colBg("#eef2ff")}>{Math.round(day.stock?.uk_cityplay_l ?? 0)}</td>
                  <td style={colBg("#eef2ff")}>{Math.round(day.stock?.amazon?.uk_cityplay_l ?? 0)}</td>

                  {/* 🇬🇧 UK 2.0 */}
                  <td style={colBg("#eef2ff")}>{Math.round(day.stock?.uk_20_m ?? 0)}</td>
                  <td style={colBg("#eef2ff")}>{Math.round(day.stock?.amazon?.uk_20_m ?? 0)}</td>
                  <td style={colBg("#eef2ff")}>{Math.round(day.stock?.uk_20_l ?? 0)}</td>
                  <td style={colBg("#eef2ff")}>{Math.round(day.stock?.amazon?.uk_20_l ?? 0)}</td>

                  {/* Total */}
                  <td style={colBg("#dcfce7")}>{Math.round(day.stock?.total_stock ?? 0)}</td>
                </>
              )}



                {/* Shipment */}
                {visibleSections.shipment && (
                  <>
                    <td style={colBg("#fdf2f8")}>
                      {day.shipment?.us?.worst_delivery_delay ?? "-"}
                    </td>
                    <td style={colBg("#fdf2f8")}>
                      {day.shipment?.us?.worst_delivery_date ?? "-"}
                    </td>
                    <td style={colBg("#fdf2f8")}>
                      {day.shipment?.eu?.worst_delivery_delay ?? "-"}
                    </td>
                    <td style={colBg("#fdf2f8")}>
                      {day.shipment?.eu?.worst_delivery_date ?? "-"}
                    </td>
                  </>
                )}

                {/* Duration */}
                {visibleSections.duration && (
                  <>
                    <td style={colBg("#fff1f2")}>
                      {day.shipment?.duration_days?.china_us ?? "-"}
                    </td>
                    <td style={colBg("#fff1f2")}>
                      {day.shipment?.duration_days?.china_uk ?? "-"}
                    </td>
                    <td style={colBg("#fff1f2")}>
                      {day.shipment?.duration_days?.us_customer ?? "-"}
                    </td>
                    <td style={colBg("#fff1f2")}>
                      {day.shipment?.duration_days?.uk_customer ?? "-"}
                    </td>
                  </>
                )}

                {/* Actions */}
                <td style={{ textAlign: "center" }}>
                  <div
                    style={{
                      display: "flex",
                      gap: "0.5rem",
                      justifyContent: "center",
                    }}
                  >
                    <button onClick={() => onEditDay(day)} style={editBtn}>
                      <Edit size={14} />
                    </button>
                    <button onClick={() => handleDelete(day)} style={deleteBtn}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 📊 Projected Inventory Chart */}
      <div style={{ marginTop: "2rem" }}>
        <ProjectedInventoryChart data={filteredData} />
      </div>

    </div>
  );
};

// ===== Styles =====
const settingsBtn = {
  float: "right",
  background: "none",
  border: "none",
  cursor: "pointer",
};
const settingsMenu = {
  position: "absolute",
  right: "1.5rem",
  top: "3rem",
  background: "white",
  border: "1px solid #e5e7eb",
  borderRadius: "8px",
  padding: "0.75rem 1rem",
  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
  zIndex: 200,
};
const stickyHeader = {
  position: "sticky",
  top: 0,
  zIndex: 150,
  background: "#f8fafc",
  padding: "1rem 1.5rem",
  borderBottom: "1px solid #e5e7eb",
  fontWeight: 600,
  boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
};
const containerStyle = {
  background: "white",
  borderRadius: "8px",
  border: "1px solid #e5e7eb",
  overflow: "hidden",
  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
};
const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: "0.875rem",
};
const stickyTh = {
  position: "sticky",
  left: 0,
  background: "#f8fafc",
  border: "1px solid #e5e7eb",
  padding: "0.75rem",
  textAlign: "left",
  fontWeight: "600",
};
const stickyTd = (i) => ({
  position: "sticky",
  left: 0,
  background: i % 2 ? "#fafafa" : "white",
  border: "1px solid #e5e7eb",
  padding: "0.75rem",
});
const thGroup = (bg, color) => ({
  border: "1px solid #e5e7eb",
  background: bg,
  textAlign: "center",
  fontWeight: "600",
  color,
});
const colBg = (bg) => ({
  background: bg,
  border: "1px solid #e5e7eb",
  padding: "0.5rem",
  textAlign: "center",
});
const actionTh = {
  border: "1px solid #e5e7eb",
  background: "#f8fafc",
  textAlign: "center",
  fontWeight: "600",
};
const editBtn = {
  backgroundColor: "#3b82f6",
  color: "white",
  border: "none",
  borderRadius: "4px",
  padding: "0.5rem",
};
const deleteBtn = {
  backgroundColor: "#dc2626",
  color: "white",
  border: "none",
  borderRadius: "4px",
  padding: "0.5rem",
};
const addBtn = {
  display: "inline-flex",
  alignItems: "center",
  gap: "0.5rem",
  padding: "0.75rem 1.5rem",
  backgroundColor: "#16a34a",
  color: "white",
  border: "none",
  borderRadius: "6px",
  fontSize: "0.875rem",
  cursor: "pointer",
  fontWeight: "500",
};
const LoadingView = () => (
  <div style={containerStyle}>
    <div style={stickyHeader}>Loading planning data...</div>
    <div style={{ textAlign: "center", padding: "3rem", color: "#6b7280" }}>
      <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>⏳</div>
      Fetching data...
    </div>
  </div>
);
const EmptyView = ({ onAddFirstDay }) => (
  <div style={containerStyle}>
    <div style={stickyHeader}>Planning Data Overview (0 days)</div>
    <div style={{ textAlign: "center", padding: "3rem", color: "#6b7280" }}>
      <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>📊</div>
      <h3>No Planning Data Found</h3>
      <button onClick={onAddFirstDay} style={addBtn}>
        <Plus size={16} /> Add Your First Day
      </button>
    </div>
  </div>
);

export default PlanningTable;
